import { createClient } from '@/lib/supabase/client';

export type EmailDeliveryStatus = 'sending' | 'sent' | 'failed';

export interface EmailDeliveryResult {
  status: EmailDeliveryStatus;
  type: string;
  workOrderId: string;
  timestamp: string;
}

type DeliveryListener = (result: EmailDeliveryResult) => void;
const deliveryListeners: DeliveryListener[] = [];

export function onEmailDelivery(listener: DeliveryListener): () => void {
  deliveryListeners.push(listener);
  return () => {
    const idx = deliveryListeners.indexOf(listener);
    if (idx > -1) deliveryListeners.splice(idx, 1);
  };
}

function notifyDelivery(result: EmailDeliveryResult) {
  deliveryListeners.forEach((l) => l(result));
}

export interface WorkOrderAssignedPayload {
  workOrderId: string;
  workOrderTitle: string;
  workOrderType: string;
  priority: string;
  slaDeadline: string;
  estimatedDuration: string;
  assigneeName: string;
  assignerName?: string;
  incidentId: string;
  node: string;
  woRegion: string;
  woCustomer: string;
  recipients: string[];
}

export interface WorkOrderStatusChangePayload {
  workOrderId: string;
  workOrderTitle: string;
  oldStatus: string;
  newStatus: string;
  updatedBy: string;
  node: string;
  woRegion: string;
  priority: string;
  slaDeadline: string;
  recipients: string[];
}

export interface SlaApproachingPayload {
  workOrderId: string;
  workOrderTitle: string;
  incidentId: string;
  priority: string;
  slaDeadline: string;
  slaRemaining: string;
  assigneeName: string;
  node: string;
  woRegion: string;
  woCustomer: string;
  woStatus: string;
  recipients: string[];
}

async function callNotificationEdgeFunction(payload: Record<string, unknown>): Promise<void> {
  const supabase = createClient();
  const { data: { session } } = await supabase.auth.getSession();

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!supabaseUrl) return;

  const workOrderId = (payload.workOrderId as string) || '';
  const type = (payload.type as string) || '';

  notifyDelivery({ status: 'sending', type, workOrderId, timestamp: new Date().toISOString() });

  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/send-automation-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(payload),
    });
    notifyDelivery({
      status: res.ok ? 'sent' : 'failed',
      type,
      workOrderId,
      timestamp: new Date().toISOString(),
    });
  } catch {
    notifyDelivery({ status: 'failed', type, workOrderId, timestamp: new Date().toISOString() });
  }
}

export async function sendWorkOrderAssignedNotification(payload: WorkOrderAssignedPayload): Promise<void> {
  await callNotificationEdgeFunction({ type: 'work_order_assigned', ...payload });
}

export async function sendWorkOrderStatusChangeNotification(payload: WorkOrderStatusChangePayload): Promise<void> {
  await callNotificationEdgeFunction({ type: 'work_order_status_change', ...payload });
}

export async function sendSlaApproachingNotification(payload: SlaApproachingPayload): Promise<void> {
  await callNotificationEdgeFunction({ type: 'sla_approaching', ...payload });
}
